import * as constants from '../constants/calculatorConstants'
import {
  calculateAsRobotNode,
  processAutomationHubProduct,
  processAutomationOpsProduct,
  processOrchestratorProduct,
  processPlatformProduct,
  processDocumentUnderstandingProduct,
  processActionCenterProduct,
  processTestManagerProduct,
  processAICenterProduct,
  handleDocumentUnderstanding,
  formatCpu,
  formatBytes,
  getMultizonalFactor, calculateAllocatableCPU, calculateAllocatableMemory
} from './productUtils';
const static_reqs = require('./static-requirements.json');

enum Profile {
  EvaluationOnly = "Evaluation Only",
  HAProduction = "Production - HA Ready",
  ASAir = "AS AIR"
}

const workerType = "Worker";
const workerTypeTooltip = "Kubernetes Worker Node";
const serverType = "Server";
const serverTypeTooltip = "Kubernetes Control Plane Node";
const agentType = "Agent";
const agentTypeTooltip = "Kubernetes Worker Node";
const asRobotsType = "Dedicated";
const asRobotsTypeTooltip = "Node for Automation Suite Robots";
const duType = "Dedicated";
const duTypeTooltip = "Node for Document Understanding";
const tmType = "Dedicated";
const tmTypeTooltip = "Node for Task Mining";
const pmType = " Dedicated SQL Server";
const pmTypeTooltip = "SQL Server for Process Mining";
const nfsType = "NFS";
const nfsTypeTooltip = "NFS Server for Backup Process";
const sqlType = "Shared SQL Server";
const sqlTypeTooltip = "SQL Server for selected products";



export const calculateRequirements = (
  formData: CalculatorFormDataType
): RecommendationDataType => {

  console.log(formData);

  let profile = (() => {
    switch (formData.highLevelParameters.profile) {
      case "eval":
        return Profile.EvaluationOnly;
      case "ha":
        return Profile.HAProduction;
      default:
        return Profile.ASAir;
    }
  })();

  let onlyCoreProducts = true;
  let isTaskMiningEnabled = false;
  let isAutomationSuiteRobotsEnabled = false;
  let isProcessMiningEnabled = false;
  let dudisk: string[] = [];
  let tmdisk: string[] = [];
  let sqldisk: string[] = [];
  let pmdisk: string[] = [];
  let asRobotdisk: string[] = [];

  if (profile !== Profile.ASAir) {
    dudisk.push("256 GiB SSD");
    tmdisk.push("256 GiB SSD");
    sqldisk.push("256 GiB SSD");
  }

  const duNode = (count: number): NodeDataType => {
    return {
      nodeType: duType,
      nodeTypeTooltip: duTypeTooltip,
      nodeCount: count.toString(),
      cpu: "8 vCPU",
      ram: "52 GiB",
      drives: dudisk,
      gpu: "NVIDIA"
    }
  };

  const tmNode: NodeDataType = {
    nodeType: tmType,
    nodeTypeTooltip: tmTypeTooltip,
    nodeCount: "1",
    cpu: "20 vCPU",
    ram: "60 GiB",
    drives: tmdisk,
  };

  const nfsNode: NodeDataType = {
    nodeType: nfsType,
    nodeTypeTooltip: nfsTypeTooltip,
    nodeCount: "1",
    cpu: "4 vCPU",
    ram: "8 GiB",
    drives: [
      "2 TiB SSD"
    ],
  };

  const sqlNode = {
    nodeType: sqlType,
    nodeTypeTooltip: sqlTypeTooltip,
    nodeCount: "1",
    cpu: "8 vCPU",
    ram: "32 GiB",
    drives: sqldisk,
  };

  function calculatePMNode(formData: CalculatorFormDataType, isProcessMiningEnabled: boolean): NodeDataType {
    let pmDiskSpace = 0;
    let pmTempDbSize = 0;
    let pmRam = 0;
    if (isProcessMiningEnabled) {
      let pmProduct = formData.products.filter(p => p.key == constants.PROCESS_MINING_PRODUCT_OPTION.key)[0];
      if (pmProduct.properties != undefined) {
        let base = pmProduct.properties["eventsInMillions"] * pmProduct.properties["caseAndEventFields"] * 0.012;
        pmDiskSpace = Math.ceil(base * 12);
        pmTempDbSize = Math.ceil(base * 3);
        pmRam = Math.ceil((base * 2) + 16);
      }
    }

    if (profile !== Profile.ASAir) {
      pmdisk.push(Math.max(pmDiskSpace, 256) + " GiB Disk Space", Math.max(pmTempDbSize, 128) + " GiB TempDb Database Size");
    }

    const pmNode: NodeDataType = {
      nodeType: pmType,
      nodeTypeTooltip: pmTypeTooltip,
      nodeCount: "1",
      cpu: "16 vCPU (Minimum: 8 vCPU)",
      ram: Math.max(pmRam, 32) + " GiB",
      drives: pmdisk,
    };
    return pmNode;
  }

  for (let product of formData.products) {
    if (product.isCoreProduct === 0) {
      onlyCoreProducts = false;
    }
    if (product.key === constants.TASK_MINING_PRODUCT_OPTION.key) {
      isTaskMiningEnabled = true;
    }
    if (product.key === constants.AUTOMATION_SUITE_ROBOTS_PRODUCT_OPTION.key) {
      isAutomationSuiteRobotsEnabled = true;
    }
    if (product.key === constants.PROCESS_MINING_PRODUCT_OPTION.key) {
      isProcessMiningEnabled = true;
    }
  }

  let osDisk = "256 GiB SSD";

  // Document Understanding GPU recommendation
  const {duGpuCount, retrainingRequired, ocrGpuRequired} = handleDocumentUnderstanding(formData);

  // Automation Suite Robots additional node recommendation
  const {
    asRobotNodeCpu,
    asRobotNodeRam,
    asRobotNodeDisk,
    isAdditionalNodeNeededEval
  } = calculateAsRobotNode(formData, isAutomationSuiteRobotsEnabled);


  // Process Mining additional sql requirements
  const pmNode = calculatePMNode(formData, isProcessMiningEnabled);


  let details = new Array<string>();
  let dataDisk = "512 GiB SSD";

  // Single-node Evaluation Only
  if (profile === Profile.EvaluationOnly) {
    details.push("## Server Node")
    details.push("The [Single-node Evaluation Only](https://docs.uipath.com/automation-suite/docs/single-node-evaluation-profile-requirements-and-installation) profile only supports [2 server node configurations](https://docs.uipath.com/automation-suite/docs/single-node-evaluation-profile-requirements-and-installation#hardware-requirements): **16 vCPU / 32 GiB** and **32 vCPU / 64 GiB**.")

    let cpu = (onlyCoreProducts ? 16 : 32) * 1000;
    let ram = (onlyCoreProducts ? 32 : 64) * 1024 * 1024 * 1024;
    if (onlyCoreProducts) {
      details.push(`In current case, only core products are selected so a single **${formatCpu(cpu)}** / **${formatBytes(ram)}** server node is required.`)
      details.push(`Or you can substitute it with one server node and one agent node of **${formatCpu(cpu/2)}** / **${formatBytes(ram/2)}** each.`)
    } else {
      details.push(`In current case, because ${formData.products.filter(p => !p.isCoreProduct).map(p => "**" + p.name + "**").join(", ")} ${formData.products.filter(p => !p.isCoreProduct).length > 1 ? "are" : "is"} selected, a single **${formatCpu(cpu)}** / **${formatBytes(ram)}** server node is required.`)
      details.push(`Or you can substitute it with one server node and three agent nodes of **${formatCpu(cpu/4)}** / **${formatBytes(ram/4)}** each.`)
    }
    let nodes = new Array<NodeDataType>();
    let sqlNodes = new Array<NodeDataType>()
    nodes.push({
      nodeType: serverType,
      nodeTypeTooltip: serverTypeTooltip,
      nodeCount: "1",
      cpu: formatCpu(cpu),
      ram: formatBytes(ram),
      drives: [
        "16 GiB SSD",
        osDisk,
        dataDisk,
      ],
    });


    if (isTaskMiningEnabled || duGpuCount > 0 || isAdditionalNodeNeededEval) {
      details.push("## Dedicated Nodes")
    }

    if (isTaskMiningEnabled) {
      nodes.push(tmNode);
      details.push(`* **Task Mining** needs a [dedicated agent](https://docs.uipath.com/automation-suite/docs/adding-a-dedicated-agent-node-for-task-mining) node.`)
    }
    if (duGpuCount > 0) {
      nodes.push(duNode(duGpuCount))
      if (retrainingRequired) {
        details.push(`* **Document Understanding** needs a [dedicated agent](https://docs.uipath.com/automation-suite/docs/adding-a-dedicated-agent-node-with-gpu-support) node for **retraining**.`)
      }
      if (ocrGpuRequired) {
        details.push(`* **Document Understanding** needs a [dedicated agent](https://docs.uipath.com/automation-suite/docs/adding-a-dedicated-agent-node-with-gpu-support) node to handle more than **60 OCR pages per hour**.`)
      }
    }
    if (isAdditionalNodeNeededEval) {
      nodes.push({
        nodeType: asRobotsType,
        nodeTypeTooltip: asRobotsTypeTooltip,
        nodeCount: "1",
        cpu: formatCpu(asRobotNodeCpu),
        ram: formatBytes(asRobotNodeRam),
        drives: [
          "256 GiB SSD",
          asRobotNodeDisk + " GiB SSD"
        ],
      });
      details.push(`* **Automation Suite Robots** needs a [dedicated agent](https://docs.uipath.com/automation-suite/v2022.10/docs/hardware-and-software-requirements#additional-automation-suite-robots-requirements) node.`)
    }

    if (isProcessMiningEnabled) {
      sqlNodes.push(pmNode);
      details.push("## Separated SQL Server")
      details.push(`* **Process Mining** needs a [separate Microsoft SQL Server](https://docs.uipath.com/automation-suite/v2022.10/docs/multi-node-configuring-ms-sql-server#sql-requirements-for-process-mining).`)
    }

    // SQL Server node for Products SQL requirements
    sqlNodes.push(sqlNode);

    details.push("### Each Server Node Configuration")
    details.push(`* Requires **${formatCpu(cpu)}** processors.`)
    details.push(`* Requires **${formatBytes(ram)}** RAM.`)
    details.push(`* Requires **16 GiB SSD** Disk dedicated for storing **etcd** data.`)
    details.push(`* Requires **${osDisk}** Disk dedicated for storing **cluster state** data.`)
    details.push(`* Requires **${dataDisk}** Disk dedicated for storing **block** data.`)
    if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3) {
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk dedicated for storing **objectstore** data.`)
    }
    if (formData.highLevelParameters.airGapped) {
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk for temporarily unbundling the **offline** packages. This disk is only on the first server machine.`)
    }


    let recommendation : RecommendationDataType = {
      nodes: nodes,
      debug: details,
      sqlNodes: sqlNodes
    }

    if (formData.highLevelParameters.backupEnabled) {
      recommendation["nfsNode"] = nfsNode;
    }

    return recommendation;
  }

  // Multi-nodes Production, HA ready
  let totalCpu = 0;
  let totalRam = 0;
  let totalDataDisk = 0;
  let totalPvc = 0;

  let faultCount = +formData.highLevelParameters.faultCount;
  console.log("highLevelParameters:", formData.highLevelParameters);
  let serverCount = 3;
  if (profile === Profile.ASAir) {
    serverCount = 4;
  }

  details.push("## High Availability")
  if (faultCount == 0) {
    details.push("A node fault tolerance of **0** doesn't mean the cluster won't survive one node going down. The cluster will still be functional but it will be in a degraded state (some services might be unavailable or with reduced capacity) because the total capacity of the cluster without that missing node is less than the sum of the capacity needed by each component (see below).")
  } else {
    // Let X be the number of server nodes and F be the number of faults.
    // To survive F faults, the quorum (X/2+1) needs to be guaranteed, so X should be greater or equal to 2*F+1
    serverCount = Math.max(serverCount, 2 * faultCount + 1);
    details.push(`To survive **${faultCount}** nodes going down:`);
    if(profile !== Profile.ASAir) {
      details.push(`* The cluster will need **${serverCount}** server nodes because in the worst case, these **${faultCount}** nodes will be server nodes which need to maintain a quorum of **${Math.floor(serverCount / 2) + 1}**.`);
      details.push(`* With **S** servers and **A** agent nodes, **S+A-${faultCount}** nodes need to have enough capacity to accomodate the resource requirements (see below).`)
      details.push(`* If dedicated nodes need to also be highly available, then they should also increase their count by **${faultCount}**. That being said, typically these node run background jobs, so real HA is not required.`)
    }else{
      details.push(`* The cluster will need **${serverCount}** worker nodes to maintain a quorum of **${Math.floor(serverCount / 2) + 1}**.`);
      details.push(`* With **W** worker nodes, **W-${faultCount}** nodes need to have enough capacity to accomodate the resource requirements (see below).`)
      details.push(`* If dedicated nodes need to also be highly available, then they should also increase their count by **${faultCount}**. That being said, typically these node run background jobs, so real HA is not required.`)

    }
  }

  details.push("## Resource Requirements")
  details.push("### Per Service")

  const productHandlers = {
    [constants.ORCHESTRATOR_PRODUCT_OPTION.key]: processOrchestratorProduct,
    [constants.PLATFORM_PRODUCT_OPTION.key]: processPlatformProduct,
    [constants.AUTOMATION_OPS_PRODUCT_OPTION.key]: processAutomationOpsProduct,
    [constants.AUTOMATION_HUB_PRODUCT_OPTION.key]: processAutomationHubProduct,
    [constants.DOCUMENT_UNDERSTANDING_PRODUCT_OPTION.key]: processDocumentUnderstandingProduct,
    [constants.ACTION_CENTER_PRODUCT_OPTION.key]: processActionCenterProduct,
    [constants.TEST_MANAGER_PRODUCT_OPTION.key]: processTestManagerProduct,
    [constants.AI_CENTER_PRODUCT_OPTION.key]: processAICenterProduct
  };

  for (let product of formData.products) {
    let product_reqs = static_reqs[product.key][profile];
    totalCpu += product_reqs?.cpu ?? 0;
    totalRam += product_reqs?.ram ?? 0;

    details.push(`#### ${product.name}`);
    
    // Add AI Center specific note as the first point
    if (product.key === constants.AI_CENTER_PRODUCT_OPTION.key) {
      details.push(`* **Note:** Only incase you have overridden CPU and Memory limits for your AI Center Skill. Use VM with CPU = Skill CPU size + 2 vCPU and RAM = Skill RAM size + 2GB based on your requirement.`);
    }
    
    details.push(`* Needs **${formatCpu(product_reqs?.cpu ?? 0, 1)}** / **${formatBytes((product_reqs?.ram ?? 0) * 1024 * 1024, 1)}** for the minimum number of replicas.`);

    console.log("Product:", product.name, ", Total CPU: ", totalCpu)

    const handler = productHandlers[product.key];
    if (handler) {
      const result = handler(product, details, totalCpu, totalRam, totalPvc);
      if (result) {
        totalCpu = result.totalCpu;
        totalRam = result.totalRam;
        totalPvc = result.totalPvc;
        }
    }
  }

  // Considering Fabric a seperate product from Shared Suite Capabilities
  let fabric_reqs = static_reqs["fabric"][profile];
  totalCpu += fabric_reqs?.cpu ?? 0;
  totalRam += fabric_reqs?.ram ?? 0;

  details.push(`#### Fabric`);
  details.push(`* Needs **${formatCpu(fabric_reqs?.cpu ?? 0, 1)}** / **${formatBytes((fabric_reqs?.ram ?? 0) * 1024 * 1024, 1)}** for the minimum number of replicas.`);


  details.push("### Total");
  details.push("Service resource requirements:");
  details.push(`* Actual: **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}**`);

  let staticCpu = profile === Profile.HAProduction ? 600 :
      Profile.ASAir ? 100 : 0;

  let staticRam = profile === Profile.HAProduction ? 270 :
      Profile.ASAir ? 100 : 0;

  details.push("### Per Node");
  details.push("Not all the CPU / RAM of the nodes is available to run the services:")
  details.push("* Each node reserves **0.6 vCPU** / **2 GiB** for the OS.");

  if(profile === Profile.HAProduction) {
    details.push(`* Each node has static components (logging, metrics, etc...) reserving **${formatCpu(staticCpu)}** / **270 MiB**.`)
    details.push("* Each node has storage components ([Longhorn](https://longhorn.io/)) reserving **20% of the allocatable CPU**.")
  }

  if(profile === Profile.ASAir) {
    details.push(`* Each node has static components (logging, monitoring, metrics, etc...) reserving **${formatCpu(staticCpu)}** / **100 MiB**.`)
  }

  let nodePhysicalCpu = formData.highLevelParameters.coreCount * 1000;
  let nodePhysicalRam = formData.highLevelParameters.coreCount * 2 * 1024 * 1024 * 1024;


  let nodeAllocatableCpu = nodePhysicalCpu - 600;
  let nodeAllocatableRam = nodePhysicalRam - 2 * 1024 * 1024 * 1024;
  let serverNodeAvailableCpu = profile === Profile.HAProduction ? nodeAllocatableCpu * 0.8 - staticCpu : Profile.ASAir ? nodeAllocatableCpu - staticCpu: 0;
  let agentNodeAvailableCpu = profile === Profile.HAProduction ? nodeAllocatableCpu * 0.9 - staticCpu : Profile.ASAir ? nodeAllocatableCpu - staticCpu: 0;
  let nodeAvailableRam = profile === Profile.HAProduction ? nodeAllocatableRam - 270 * 1024 * 1024 : Profile.ASAir ? nodeAllocatableRam - staticRam * 1024 * 1024: 0;

  // let nodeAllocatableRam = nodePhysicalRam - 2 * 1024 * 1024 * 1024;
  // let serverNodeAvailableCpu = nodeAllocatableCpu * 0.8 - 600;
  // let agentNodeAvailableCpu = nodeAllocatableCpu * 0.9 - 600;
  // let nodeAvailableRam = nodeAllocatableRam - 270 * 1024 * 1024;
  let totalServerCPU = serverCount * serverNodeAvailableCpu;
  let totalAgentCPU = totalCpu - totalServerCPU;
  let minAgentNode = Math.ceil(totalAgentCPU / agentNodeAvailableCpu);
  let minCount = 0
  // Resource requirements in case of multizonal cluster
  if (formData.highLevelParameters.isMultizoanl) {
    let minCount = Math.ceil(Math.max(serverCount + minAgentNode, totalRam / nodeAvailableRam));
    let nodeCount = Math.max(serverCount, minCount + faultCount);
    let agentCount = Math.max(0, nodeCount - serverCount);
    const zoneFailureDelta = getMultizonalFactor(nodeCount);
    totalCpu *= zoneFailureDelta;
    totalRam *= zoneFailureDelta;
  }

  // Add some breathing room
  totalCpu *= 1.20;
  totalRam *= 1.20;
  details.push(`* With 20% buffer: **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}**`);
  if (totalPvc > 0) {
    details.push(`* Handling AI Center pipelines requires an additional **${totalPvc}** GiB PVC.`);
  }

  totalAgentCPU = totalCpu - totalServerCPU;
  minAgentNode = Math.ceil(totalAgentCPU / agentNodeAvailableCpu);

  if (profile !== Profile.ASAir) {
   minCount = Math.ceil(Math.max(serverCount + minAgentNode, totalRam * 1024 * 1024 / nodeAvailableRam));
  }else{
    minCount = Math.ceil(Math.max(serverCount , totalRam * 1024 * 1024 / nodeAvailableRam));
  }

  let nodeCount = Math.max(serverCount, minCount + faultCount);
  let agentCount = Math.max(0, nodeCount - serverCount);
  let finalNodeCount = Math.ceil(Math.max(totalCpu/nodePhysicalCpu, totalRam * 1024 * 1024 / nodePhysicalRam))

  details.push("## Recommendation");
  if (profile !== Profile.ASAir) {
    details.push(`* A **${formatCpu(nodePhysicalCpu)}** / **${formatBytes(nodePhysicalRam)}** has an *allocatable* capacity of **${formatCpu(nodeAllocatableCpu)}** / **${formatBytes(nodeAllocatableRam)}**, of which only **${formatCpu(serverNodeAvailableCpu)}** / **${formatBytes(nodeAvailableRam)}** is *available* for server node and **${formatCpu(serverNodeAvailableCpu)}** / **${formatBytes(nodeAvailableRam)}** is *available* for agent node.`);
    details.push(`* Therefore, to schedule **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}** worth of services, at least **${minCount}** nodes are required.`);
    details.push(`* But to survive **${faultCount}** node failures, **${minCount} + ${faultCount} = ${minCount + faultCount}** nodes are required.`)
    details.push(`* Out of which, **${serverCount}** server nodes and, **${agentCount}** agent nodes are required.`)

    switch (formData.highLevelParameters.host) {
      case "AWS":
        details.push(`* You will need a shared **SQL server** and **AWS S3 Buckets** for the selected products.`);
        break;
      case "Azure":
        details.push(`* You will need a shared **SQL server** and **Azure Blob Storage** for the selected products.`);
        break;
      case "On-Prem":
        if (formData.highLevelParameters.externalS3) {
          details.push(`* You will need a shared **SQL server** and **S3 Compatible Object Store** for the selected products.`);
        } else {
          details.push(`* You will need a shared **SQL server** for the selected products.`);
        }
        break;
    }
  }else{

    details.push(`* A **${formatCpu(nodePhysicalCpu)}** / **${formatBytes(nodePhysicalRam)}** has an *allocatable* capacity of **${formatCpu(nodeAllocatableCpu)}** / **${formatBytes(nodeAllocatableRam)}**, of which only  **${formatCpu(agentNodeAvailableCpu)}** / **${formatBytes(nodeAvailableRam)}** is *available* for a worker node.`);
    details.push(`* Therefore, to schedule **${formatCpu(totalCpu)}** / **${formatBytes(totalRam * 1024 * 1024)}** worth of services, at least **${finalNodeCount}** nodes are required.`);
    details.push(`* But to survive **${faultCount}** node failures, **${finalNodeCount + faultCount}** nodes are required.`)

    switch (formData.highLevelParameters.profile) {
      case "air-eks":
        details.push(`* You will need a shared **SQL server** and **AWS S3 Buckets** for the selected products.`);
        details.push(`* You will need a **100 GiB** of **EBS** for monitoring and **20 GiB** of **EBS** for logging .`);
        break;
      case "air-aks":
        details.push(`* You will need a shared **SQL server** and **Azure Blob Storage** for the selected products.`);
        details.push(`* You will need a **100 GiB** of **Azure Manage Disk** for monitoring and **20 GiB** of **Azure Manage Disk** for logging .`);
        break;
    }
  }
  // Rounding up
  totalDataDisk = Math.ceil(totalDataDisk);

  let nodes = new Array<NodeDataType>();
  let sqlNodes = new Array<NodeDataType>();
  let drivesArray = [];
  let type = ""
  let toolTip = ""
  let count = ""
  if (profile !== Profile.ASAir) {
    drivesArray.push("16 GiB SSD", osDisk, dataDisk);
    type = serverType
    toolTip = serverTypeTooltip
    count = agentCount > 0 ? (3 + faultCount ).toString(): faultCount != 0 ? (minCount + faultCount).toString() :  minCount.toString()
  }else{
    type = workerType
    toolTip = workerTypeTooltip
    count = faultCount != 0 ? (finalNodeCount + faultCount).toString() :  finalNodeCount.toString()
  }
  nodes.push({
    nodeType: type,
    nodeTypeTooltip: toolTip,
    nodeCount: count,
    nodeCountTooltip: `An odd number of Server nodes. At least ${serverCount}.`,
    cpu: formatCpu(nodePhysicalCpu),
    ram: formatBytes(nodePhysicalRam),
    drives: drivesArray,
  });

  if (profile !== Profile.ASAir) {
    if (agentCount > 0) {
      nodes.push({
        nodeType: agentType,
        nodeTypeTooltip: agentTypeTooltip,
        nodeCount: agentCount.toString(),
        nodeCountTooltip: `See details below.`,
        cpu: formatCpu(nodePhysicalCpu),
        ram: formatBytes(nodePhysicalRam),
        drives: [
          "256 GiB SSD",
        ],
      });
    }
  }

  if (isTaskMiningEnabled) {
    nodes.push(tmNode);
    details.push(`* **Task Mining** needs a dedicated node.`)
  }
  if (duGpuCount > 0) {
    nodes.push(duNode(duGpuCount))
    details.push(`* **Document Understanding** needs a dedicated node.`)
  }

  if (profile !== Profile.ASAir) {
    asRobotdisk.push("256 GiB SSD", asRobotNodeDisk + " GiB SSD");
  }

  if (isAutomationSuiteRobotsEnabled) {
    nodes.push({
      nodeType: asRobotsType,
      nodeTypeTooltip: asRobotsTypeTooltip,
      nodeCount: "1",
      cpu: Math.max(formData.highLevelParameters.coreCount,parseFloat((asRobotNodeCpu / 1000.0).toFixed(1))) + " vCPU",
      ram: formatBytes(asRobotNodeRam),
      drives: asRobotdisk,
    });
    details.push(`* **Automation Suite Robots** needs a dedicated node and **10 GiB** of file storage`)
  }

  if (isProcessMiningEnabled) {
    sqlNodes.push(pmNode);
    details.push(`* **Process Mining** needs a [separate Microsoft SQL Server](https://docs.uipath.com/automation-suite/v2022.10/docs/multi-node-configuring-ms-sql-server#sql-requirements-for-process-mining).`)
  }

  // SQL Server node for Products SQL requirements
  sqlNodes.push(sqlNode);

  let recommendation : RecommendationDataType = {
    nodes: nodes,
    debug: details,
    sqlNodes: sqlNodes
  }

  if (formData.highLevelParameters.backupEnabled && profile !== Profile.ASAir) {
    recommendation["nfsNode"] = nfsNode;
    details.push(`* **Enabling Backup** will need an NFS Server or equivalent PaaS file share.`)
  }
  if (profile !== Profile.ASAir) {
    details.push("### Each Server Node Configuration")
    details.push(`* Requires **${formatCpu(nodePhysicalCpu)}** processors.`);
    details.push(`* Requires **${formatBytes(nodePhysicalRam)}** RAM.`);
    details.push(`* Requires **16 GiB SSD** Disk dedicated for storing **etcd** data.`);
    details.push(`* Requires **${osDisk}** Disk dedicated for storing **cluster state** data.`);
    details.push(`* Requires **${dataDisk}** Disk dedicated for storing **block** data.`);
  }else {
    details.push("### Each Worker Node Configuration")
    details.push(`* Requires **${formatCpu(nodePhysicalCpu)}** processors.`);
    details.push(`* Requires **${formatBytes(nodePhysicalRam)}** RAM.`);
  }
  if (profile !== Profile.ASAir) {
    if (formData.highLevelParameters.host === "On-Prem" && !formData.highLevelParameters.externalS3) {
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk dedicated for storing **objectstore** data.`);
    }
    if (formData.highLevelParameters.airGapped) {
      nodes[0].drives.push("512 GiB SSD");
      details.push(`* Requires **512 GiB SSD** Disk for temporarily unbundling the **offline** packages. This disk is only on the first server machine.`);
    }
  }
  if (profile !== Profile.ASAir) {
    if (agentCount > 0) {
      details.push("### Each Agent Node Configuration")
      details.push(`* Requires **${formatCpu(nodePhysicalCpu)}** processors.`)
      details.push(`* Requires **${formatBytes(nodePhysicalRam)}** RAM.`)
      details.push(`* Requires **256 GiB SSD** Disk dedicated for storing **cluster state** data.`)
    }

    details.push(`> **etcd**, **block**, and **objectstore** disk must be a dedicated disk on each server node, the rest can be merged together or even with the operating system disk.`)
  }
  return recommendation;
};
